// One depot account (carrier x warehouse) inside a CarrierCard: login
// credentials, collection window, test-connection, and the bookable
// services table. APC issued separate accounts for Newport and
// Pembroke Dock so consignments book against the right depot's
// operational address — hence per-warehouse panels rather than one
// credential set on the carrier.

import { useState } from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { useTranslation } from "../../../i18n";
import { apiFetch } from "../../../api/apiFetch";
import { useReadOnly } from "../../../access/ReadOnly";
import SecretField from "./SecretField";
import CarrierSection from "./CarrierSection";
import RabenAccountFields from "./RabenAccountFields";
import { base64ToBytes, downloadPdfBase64 } from "../../helpers/carrierLabelPrint";

import { depotPrefixFor } from "./apcServiceCatalogue";
import { catalogueFor, credentialKindFor, isConnectionOnly, itemTypesFor, usesApiKey, usesBandPricing, usesLoadingPlace, usesOAuth, usesSenderAddress } from "./carrierCatalogues";
import type {
    CarrierAccount,
    CarrierAccountPatch,
    CarrierConfig,
    CarrierEnvironment,
    CarrierService,
    CarrierServicePatch,
    CarrierServicePricing,
    CarrierTestResult,
} from "./types";

// What POST /admin/carriers/accounts/:id/test-booking answers.
interface CarrierTestBookingResult {
    ok: boolean;
    carrier: string;
    environment: "training" | "live";
    steps: { name: string; ok: boolean; status: number | null; error: string | null; ms: number; detail?: unknown }[];
    trackingNumbers: string[];
    label: { format: "ZPL" | "PDF"; contentBase64: string; trackingNumber: string } | null;
    cleanup: { deleted: boolean | null; note: string } | null;
    warnings: string[];
    // Which service row the test booked with and how it was chosen —
    // 'shipping_rules' is exactly what the bench would do for this
    // destination; the other two are fallbacks when no rule lands here.
    service?: {
        code: string;
        name: string | null;
        source: "shipping_rules" | "account_service" | "carrier_default" | "picked";
        ruleName: string | null;
    } | null;
}

interface Props {
    account: CarrierAccount;
    // carriers.carrier_code — decides the credential block (login vs
    // OAuth), the item-type vocabulary, the tariff columns and the
    // "Add service" catalogue.
    carrierCode?: string;
    // The carrier's current environment. DPD on Training books with DPD's
    // public demo credentials (BE dpdClient DPD_DEMO_CREDENTIALS), so the
    // fields below are its LIVE credentials and the panel says so.
    environment?: CarrierEnvironment;
    onUpdated: (next: CarrierConfig) => void;
}

// Human label for an account's role on a multi-account carrier.
const accountLabelText = (label: string): string => {
    if (label === "2door") return "2Door";
    if (label === "ooh") return "OOH";
    if (label === "default") return "";
    return label;
};

const patchJson = async (path: string, method: string, body?: unknown) => {
    const res = await apiFetch(path, {
        method,
        headers: { "Content-Type": "application/json" },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json?.message || `HTTP ${res.status}`);
    return json;
};

// One editable service row. Text/number fields keep a local draft and
// commit on blur (the settings-page convention); toggles PATCH
// immediately.
const ServiceRow = ({
    service,
    carrierCode = "apc",
    onUpdated,
}: {
    service: CarrierService;
    carrierCode?: string;
    onUpdated: (next: CarrierConfig) => void;
}) => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const bandPricing = usesBandPricing(carrierCode);
    const pricing: CarrierServicePricing = service.pricing ?? {};
    const isFlat = pricing.flat != null || (pricing.bands == null && service.maxPieces === 1 && carrierCode === "inpost");

    // Band/flat price commit: merges one key into the pricing descriptor.
    const priceBlur = (band: "S" | "M" | "L" | "flat") =>
        (e: React.FocusEvent<HTMLInputElement>) => {
            const raw = e.target.value.trim();
            const next = raw === "" ? null : Math.round(Number(raw) * 100) / 100;
            if (next !== null && (!Number.isFinite(next) || next < 0)) return;
            const current = band === "flat" ? pricing.flat ?? null : pricing.bands?.[band] ?? null;
            if (next === current) return;
            const merged: CarrierServicePricing = band === "flat"
                ? { ...pricing, flat: next ?? undefined }
                : { ...pricing, bands: { ...(pricing.bands ?? {}), [band]: next ?? undefined } };
            // base_cost mirrors the lowest band so the bench box recommender
            // keeps a floor to rank on.
            const floor = merged.flat ?? merged.bands?.S ?? merged.bands?.M ?? merged.bands?.L ?? null;
            patch({ pricing: merged, baseCost: floor });
        };

    const patch = async (body: CarrierServicePatch) => {
        if (readOnly) return;
        setSaving(true);
        setError(null);
        try {
            const json = await patchJson(`/admin/carriers/services/${service.id}`, "PATCH", body);
            onUpdated(json.carrier as CarrierConfig);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (readOnly) return;
        setSaving(true);
        setError(null);
        try {
            const json = await patchJson(`/admin/carriers/services/${service.id}`, "DELETE");
            onUpdated(json.carrier as CarrierConfig);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
            setConfirmingDelete(false);
        } finally {
            setSaving(false);
        }
    };

    // Blur-commit helper for numeric fields. Empty = null (no limit /
    // no recorded cost). `decimals` keeps pounds at 2dp while limits
    // stay integers; costs allow zero (free additional parcels).
    const numBlur = (key: keyof CarrierServicePatch, current: number | null, scale = 1, decimals = 0) =>
        (e: React.FocusEvent<HTMLInputElement>) => {
            const raw = e.target.value.trim();
            const factor = 10 ** decimals;
            const next = raw === "" ? null : Math.round(Number(raw) * scale * factor) / factor;
            const allowZero = key === "baseCost" || key === "additionalParcelCost" || key === "includedKg" || key === "perKgCost";
            if (next !== null && (!Number.isFinite(next) || next < 0 || (!allowZero && next === 0))) return;
            if (next === current) return;
            patch({ [key]: next } as CarrierServicePatch);
        };

    return (
        <div className="carrier-service-row">
            <div className="carrier-service-grid">
                <label className="admin-settings-toggle" title={t("carriers_service_enabled")}>
                    <input
                        type="checkbox"
                        checked={service.enabled}
                        disabled={saving || readOnly}
                        onChange={(e) => patch({ enabled: e.target.checked })}
                    />
                    <span className="admin-settings-slider" />
                </label>
                <input
                    className="admin-integrations-input admin-integrations-input--mono"
                    defaultValue={service.serviceCode}
                    disabled={saving || readOnly}
                    onBlur={(e) => {
                        const v = e.target.value.trim().toUpperCase();
                        if (v && v !== service.serviceCode) patch({ serviceCode: v });
                    }}
                />
                <input
                    className="admin-integrations-input"
                    defaultValue={service.displayName}
                    disabled={saving || readOnly}
                    onBlur={(e) => {
                        const v = e.target.value.trim();
                        if (v && v !== service.displayName) patch({ displayName: v });
                    }}
                />
                <select
                    className="admin-integrations-input"
                    value={service.itemType}
                    disabled={saving || readOnly}
                    onChange={(e) => patch({ itemType: e.target.value })}
                >
                    {itemTypesFor(carrierCode).map((it) => (
                        <option key={it} value={it}>{it}</option>
                    ))}
                </select>
                <input
                    type="number" min={1}
                    className="admin-integrations-input"
                    defaultValue={service.maxPieces ?? ""}
                    disabled={saving || readOnly}
                    onBlur={numBlur("maxPieces", service.maxPieces)}
                />
                <input
                    type="number" min={0.01} step={0.1}
                    className="admin-integrations-input"
                    defaultValue={service.maxWeightGrams != null ? service.maxWeightGrams / 1000 : ""}
                    disabled={saving || readOnly}
                    onBlur={numBlur("maxWeightGrams", service.maxWeightGrams, 1000)}
                />
                <input
                    type="number" min={1}
                    className="admin-integrations-input"
                    defaultValue={service.maxLengthCm ?? ""}
                    disabled={saving || readOnly}
                    onBlur={numBlur("maxLengthCm", service.maxLengthCm)}
                />
                <input
                    type="number" min={1}
                    className="admin-integrations-input"
                    defaultValue={service.maxWidthCm ?? ""}
                    disabled={saving || readOnly}
                    onBlur={numBlur("maxWidthCm", service.maxWidthCm)}
                />
                <input
                    type="number" min={1}
                    className="admin-integrations-input"
                    defaultValue={service.maxHeightCm ?? ""}
                    disabled={saving || readOnly}
                    onBlur={numBlur("maxHeightCm", service.maxHeightCm)}
                />
                {bandPricing ? (
                    // InPost: per-parcel band prices (S/M/L) or one flat
                    // locker rate, plus the priority the booking sends.
                    <>
                        <select
                            className="admin-integrations-input"
                            value={pricing.priority ?? "STANDARD"}
                            disabled={saving || readOnly}
                            onChange={(e) => patch({ pricing: { ...pricing, priority: e.target.value as "STANDARD" | "EXPRESS" } })}
                            title={t("carriers_service_priority")}
                        >
                            <option value="STANDARD">STANDARD</option>
                            <option value="EXPRESS">EXPRESS</option>
                        </select>
                        {isFlat ? (
                            <>
                                <input
                                    type="number" min={0} step={0.01}
                                    className="admin-integrations-input"
                                    defaultValue={pricing.flat ?? ""}
                                    placeholder="1.90"
                                    disabled={saving || readOnly}
                                    onBlur={priceBlur("flat")}
                                    title={t("carriers_service_price_flat")}
                                />
                                <span className="carrier-service-na">—</span>
                                <span className="carrier-service-na">—</span>
                            </>
                        ) : (
                            <>
                                <input
                                    type="number" min={0} step={0.01}
                                    className="admin-integrations-input"
                                    defaultValue={pricing.bands?.S ?? ""}
                                    placeholder="S"
                                    disabled={saving || readOnly}
                                    onBlur={priceBlur("S")}
                                    title={t("carriers_service_price_s")}
                                />
                                <input
                                    type="number" min={0} step={0.01}
                                    className="admin-integrations-input"
                                    defaultValue={pricing.bands?.M ?? ""}
                                    placeholder="M"
                                    disabled={saving || readOnly}
                                    onBlur={priceBlur("M")}
                                    title={t("carriers_service_price_m")}
                                />
                                <input
                                    type="number" min={0} step={0.01}
                                    className="admin-integrations-input"
                                    defaultValue={pricing.bands?.L ?? ""}
                                    placeholder="L"
                                    disabled={saving || readOnly}
                                    onBlur={priceBlur("L")}
                                    title={t("carriers_service_price_l")}
                                />
                            </>
                        )}
                    </>
                ) : (
                    <>
                        <input
                            type="number" min={0} step={0.01}
                            className="admin-integrations-input"
                            defaultValue={service.baseCost ?? ""}
                            disabled={saving || readOnly}
                            onBlur={numBlur("baseCost", service.baseCost, 1, 2)}
                        />
                        <input
                            type="number" min={0} step={0.01}
                            className="admin-integrations-input"
                            defaultValue={service.additionalParcelCost ?? ""}
                            disabled={saving || readOnly}
                            onBlur={numBlur("additionalParcelCost", service.additionalParcelCost, 1, 2)}
                        />
                        <input
                            type="number" min={0} step={0.5}
                            className="admin-integrations-input"
                            defaultValue={service.includedKg ?? ""}
                            disabled={saving || readOnly}
                            onBlur={numBlur("includedKg", service.includedKg, 1, 1)}
                        />
                        <input
                            type="number" min={0} step={0.01}
                            className="admin-integrations-input"
                            defaultValue={service.perKgCost ?? ""}
                            disabled={saving || readOnly}
                            onBlur={numBlur("perKgCost", service.perKgCost, 1, 2)}
                        />
                    </>
                )}
                {!confirmingDelete ? (
                    <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        disabled={saving || readOnly}
                        onClick={() => setConfirmingDelete(true)}
                        aria-label={t("carriers_service_delete")}
                    >
                        <Trash2 size={14} />
                    </button>
                ) : (
                    <span className="carrier-service-confirm">
                        <button type="button" className="btn btn-sm btn-danger" disabled={saving || readOnly} onClick={remove}>
                            {t("carriers_service_delete")}
                        </button>
                        <button type="button" className="btn btn-sm" disabled={saving || readOnly} onClick={() => setConfirmingDelete(false)}>
                            {t("cancel")}
                        </button>
                    </span>
                )}
            </div>
            {error && <p className="admin-integrations-field-error">{error}</p>}
        </div>
    );
};

const CarrierAccountPanel = ({ account, carrierCode = "apc", environment, onUpdated }: Props) => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    const oauth = usesOAuth(carrierCode);
    const apiKey = usesApiKey(carrierCode);
    const connectionOnly = isConnectionOnly(carrierCode);
    // JAS / Raben: desk-booked (no services here) but its loading place is the
    // account's sender address, so that section stays.
    const loadingPlace = usesLoadingPlace(carrierCode);
    const credentialKind = credentialKindFor(carrierCode);
    const showSender = usesSenderAddress(carrierCode) && (!connectionOnly || loadingPlace);
    // The collection window is APC's manifest cutoff and DPD/Cargus's
    // day-close time; InPost has neither.
    const showCollection = credentialKind !== "oauth";
    const bandPricing = usesBandPricing(carrierCode);
    const catalogue = catalogueFor(carrierCode, account.accountLabel);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState<CarrierTestResult | null>(null);
    // Cargus: the pickup points Cargus lists for this user (Sender.LocationId)
    // and the outcome of the last nomenclature sync.
    const [pickupLocations, setPickupLocations] = useState<{ locationId: string; name: string; localityName: string; countyName: string; automaticEod: boolean }[] | null>(null);
    const [loadingLocations, setLoadingLocations] = useState(false);
    const [syncingNomenclature, setSyncingNomenclature] = useState(false);
    const [nomenclatureResult, setNomenclatureResult] = useState<string | null>(null);
    // DPD / Cargus "Test booking": a sample recipient + box, booked with
    // the real credentials against the account's environment; the BE
    // reports every carrier step and the label. Defaults are a plausible
    // in-country address so the first click is a real end-to-end probe.
    // DPD: the country picks the contract (PL → Local, other EU → INTER),
    // so it is editable to test both.
    const testDefaults = credentialKind === "cargus"
        ? { name: "Portal Test", line1: "Str. Principală nr. 1", line2: "", city: "Cluj-Napoca", county: "Cluj", postcode: "400001", phone: "0740123123", email: "", country: "RO" }
        : { name: "Portal Test", line1: "ul. Długa 12", line2: "", city: "Wrocław", county: "", postcode: "50-080", phone: "512345678", email: "", country: "PL" };
    const [testRecipient, setTestRecipient] = useState(testDefaults);
    const [testBox, setTestBox] = useState({ weight_grams: 1000, length_mm: 300, width_mm: 200, height_mm: 100 });
    const [testServiceId, setTestServiceId] = useState<string>("");
    const [testBooking, setTestBooking] = useState(false);
    const [testBookingResult, setTestBookingResult] = useState<CarrierTestBookingResult | null>(null);

    const runTestBooking = async () => {
        if (readOnly) return;
        setTestBooking(true);
        setTestBookingResult(null);
        setError(null);
        try {
            const json = await patchJson(`/admin/carriers/accounts/${account.id}/test-booking`, "POST", {
                recipient: { ...testRecipient, country: credentialKind === "cargus" ? "RO" : (testRecipient.country || "PL").toUpperCase() },
                box: testBox,
                service_id: testServiceId || null,
                format: credentialKind === "cargus" ? "PDF" : "ZPL",
                // Never a real shipment from Settings: Cargus runs its
                // read-only checks, DPD only completes on the demo host.
                mode: "dry_run",
            });
            setTestBookingResult(json as CarrierTestBookingResult);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setTestBooking(false);
        }
    };

    const downloadTestLabel = (label: NonNullable<CarrierTestBookingResult["label"]>) => {
        if (label.format === "PDF") {
            downloadPdfBase64(label.contentBase64, `test-label-${label.trackingNumber}.pdf`);
            return;
        }
        const bytes = base64ToBytes(label.contentBase64);
        const url = URL.createObjectURL(new Blob([bytes], { type: "text/plain" }));
        const a = document.createElement("a");
        a.href = url;
        a.download = `test-label-${label.trackingNumber}.zpl`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    };

    const loadPickupLocations = async () => {
        setLoadingLocations(true);
        setError(null);
        try {
            const json = await patchJson(`/admin/carriers/accounts/${account.id}/pickup-locations`, "GET");
            setPickupLocations(json.locations ?? []);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setLoadingLocations(false);
        }
    };

    const syncNomenclature = async () => {
        if (readOnly) return;
        setSyncingNomenclature(true);
        setNomenclatureResult(null);
        setError(null);
        try {
            const json = await patchJson(`/admin/carriers/accounts/${account.id}/nomenclature/sync`, "POST", {});
            setNomenclatureResult(t("carriers_nomenclature_synced", { counties: String(json.counties ?? 0), localities: String(json.localities ?? 0) }));
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setSyncingNomenclature(false);
        }
    };
    const [addingService, setAddingService] = useState(false);
    // "Add service" picker state. Choosing a catalogue entry pre-fills
    // the (still editable) code with the depot prefix (NPND16 / PDMP16 —
    // APC named JLI's per-account services that way), the display name,
    // the item type and the tariff limits. "custom" leaves it all free.
    const [newCatalogueCode, setNewCatalogueCode] = useState("");
    const [newCode, setNewCode] = useState("");
    const [newName, setNewName] = useState("");
    const [newBaseCost, setNewBaseCost] = useState("");
    const [newAddCost, setNewAddCost] = useState("");

    const pickCatalogueService = (code: string) => {
        setNewCatalogueCode(code);
        const entry = catalogue.find((s) => s.code === code);
        if (entry) {
            setNewCode(`${carrierCode === "apc" ? depotPrefixFor(account.warehouse) : ""}${entry.code}`);
            setNewName(entry.name);
            setNewBaseCost(entry.baseCost != null ? String(entry.baseCost) : "");
            setNewAddCost(entry.additionalParcelCost != null ? String(entry.additionalParcelCost) : "");
        } else {
            setNewCode("");
            setNewName("");
            setNewBaseCost("");
            setNewAddCost("");
        }
    };

    const patch = async (body: CarrierAccountPatch) => {
        if (readOnly) return;
        setSaving(true);
        setError(null);
        try {
            const json = await patchJson(`/admin/carriers/accounts/${account.id}`, "PATCH", body);
            onUpdated(json.carrier as CarrierConfig);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
            throw err;
        } finally {
            setSaving(false);
        }
    };

    const runTest = async () => {
        if (readOnly) return;
        setTesting(true);
        setTestResult(null);
        try {
            const json = await patchJson(`/admin/carriers/accounts/${account.id}/test`, "POST", {});
            setTestResult(json as CarrierTestResult);
        } catch (err) {
            setTestResult({
                ok: false,
                status: null,
                latencyMs: 0,
                error: err instanceof Error ? err.message : String(err),
                environment: "training",
            });
        } finally {
            setTesting(false);
        }
    };

    const addService = async () => {
        if (readOnly) return;
        const code = newCode.trim().toUpperCase();
        const name = newName.trim();
        if (!code || !name) return;
        setSaving(true);
        setError(null);
        const entry = catalogue.find((s) => s.code === newCatalogueCode) ?? null;
        const pounds = (raw: string) => {
            const n = Number(raw);
            return raw.trim() !== "" && Number.isFinite(n) && n >= 0 ? Math.round(n * 100) / 100 : null;
        };
        try {
            const json = await patchJson(`/admin/carriers/accounts/${account.id}/services`, "POST", {
                serviceCode: code,
                displayName: name,
                // Catalogue entries carry the carrier's published tariff
                // limits; custom services start unbounded and get edited
                // inline.
                ...(entry ? {
                    itemType: entry.itemType,
                    maxPieces: entry.maxPieces,
                    maxWeightGrams: entry.maxWeightGrams,
                    maxLengthCm: entry.maxLengthCm,
                    maxWidthCm: entry.maxWidthCm,
                    maxHeightCm: entry.maxHeightCm,
                    includedKg: entry.includedKg,
                    perKgCost: entry.perKgCost,
                    ...(entry.pricing ? { pricing: entry.pricing } : {}),
                } : {}),
                // A custom InPost service starts with the typed price as
                // its Small band so it is priceable straight away.
                ...(!entry && bandPricing && pounds(newBaseCost) !== null
                    ? { pricing: { priority: "STANDARD", bands: { S: pounds(newBaseCost) } } }
                    : {}),
                baseCost: pounds(newBaseCost),
                additionalParcelCost: pounds(newAddCost),
            });
            onUpdated(json.carrier as CarrierConfig);
            setNewCatalogueCode("");
            setNewCode("");
            setNewName("");
            setNewBaseCost("");
            setNewAddCost("");
            setAddingService(false);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setSaving(false);
        }
    };

    const statusPill = account.enabled
        ? "admin-integrations-status-pill admin-integrations-status-pill--ok"
        : "admin-integrations-status-pill admin-integrations-status-pill--off";

    // One-line digests for the collapsed sections, so a closed panel
    // still reads as a status report.
    const enabledServices = account.services.filter((s) => s.enabled);
    const servicesDigest = account.services.length === 0
        ? t("carriers_services_empty")
        : `${enabledServices.map((s) => s.serviceCode).join(", ")}${enabledServices.length < account.services.length ? ` (+${account.services.length - enabledServices.length} ${t("carriers_badge_off").toLowerCase()})` : ""}`;
    const lastTest = account.status.lastTestAt
        ? (account.status.lastTestStatus === "ok" ? t("carriers_last_test_ok") : t("carriers_test_failed"))
        : null;
    const credentialsDigest = [
        apiKey ? null : oauth ? account.clientId : credentialKind === "email" ? account.loginEmail : account.loginUsername,
        lastTest,
    ].filter(Boolean).join(" · ") || null;
    const senderDigest = [
        [account.senderFirstName, account.senderLastName].filter(Boolean).join(" "),
        [account.originHouseNumber, account.originStreet].filter(Boolean).join(" "),
        account.originPostcode,
    ].filter(Boolean).join(" · ") || null;
    const collectionDigest = (account.readyAt || account.closedAt)
        ? `${t("carriers_ready_at")} ${account.readyAt ?? "—"} · ${t("carriers_closed_at")} ${account.closedAt ?? "—"}`
        : null;
    const customsDigest = [account.eoriNumber, account.dutyDescription, account.dutyHtsCode].filter(Boolean).join(" · ") || null;

    return (
        <details className="admin-integrations-vendors-details" id={`carrier-account-${account.id}`}>
            <summary className="admin-integrations-vendors-summary">
                <span>
                    {t("carriers_account_heading", { warehouse: account.warehouse })}
                    {accountLabelText(account.accountLabel) && (
                        <span className="carrier-account-label"> · {accountLabelText(account.accountLabel)}</span>
                    )}
                    {account.deliveryMode === "point" && (
                        <span className="carrier-account-label"> · {t("carriers_delivery_mode_point")}</span>
                    )}
                </span>
                <span className="carrier-section-digest carrier-account-digest">
                    {connectionOnly
                        ? (account.password.set ? lastTest : t("carriers_badge_needs_secret"))
                        : (
                            <>
                                {t("carriers_account_digest", { services: String(enabledServices.length) })}
                                {!account.password.set && ` · ${t("carriers_badge_needs_secret")}`}
                            </>
                        )}
                </span>
                <span className={statusPill}>
                    {account.enabled ? t("integrations_status_pill_enabled") : t("integrations_status_pill_disabled")}
                </span>
            </summary>
            <div className="admin-integrations-vendors-body">
                <div className="admin-integrations-form-row--toggle">
                    <label className="admin-settings-toggle">
                        <input
                            type="checkbox"
                            checked={account.enabled}
                            disabled={saving || readOnly}
                            onChange={(e) => { patch({ enabled: e.target.checked }).catch(() => {}); }}
                        />
                        <span className="admin-settings-slider" />
                    </label>
                    <span className="admin-integrations-toggle-label">
                        {t("carriers_account_enabled_toggle")}
                    </span>
                </div>

                <CarrierSection
                    title={t("carriers_section_credentials")}
                    badge={account.password.set
                        ? { text: t("carriers_badge_secret_set"), tone: "ok" }
                        : { text: t("carriers_badge_needs_secret"), tone: "warn" }}
                    summary={credentialsDigest}
                    defaultOpen={!account.password.set}
                >
                {apiKey ? (
                    // One API key per account (Palletways) — no login, no
                    // client id. Same masked cipher path as a password.
                    <SecretField
                        label={t("carriers_api_key")}
                        hint={t("carriers_api_key_hint")}
                        secret={account.password}
                        minLength={1}
                        onSave={(plaintext) => patch({ password: plaintext })}
                        onClear={() => patch({ password: null })}
                    />
                ) : oauth ? (
                    <>
                        {/* OAuth client credentials (InPost): client id +
                            organisation ("Brand") id in the clear, the
                            client secret through the same masked cipher
                            path as a password. */}
                        <div className="carrier-account-times">
                            <div className="admin-integrations-field">
                                <label className="admin-integrations-field-label">{t("carriers_client_id")}</label>
                                <input
                                    className="admin-integrations-input admin-integrations-input--mono"
                                    defaultValue={account.clientId}
                                    placeholder="app-xxxxxxxxxx"
                                    disabled={saving || readOnly}
                                    onBlur={(e) => {
                                        const v = e.target.value.trim();
                                        if (v !== account.clientId) patch({ clientId: v || null }).catch(() => {});
                                    }}
                                />
                            </div>
                            <div className="admin-integrations-field">
                                <label className="admin-integrations-field-label">{t("carriers_organization_id")}</label>
                                <input
                                    className="admin-integrations-input admin-integrations-input--mono"
                                    defaultValue={account.organizationId}
                                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                                    disabled={saving || readOnly}
                                    onBlur={(e) => {
                                        const v = e.target.value.trim();
                                        if (v !== account.organizationId) patch({ organizationId: v || null }).catch(() => {});
                                    }}
                                />
                            </div>
                        </div>
                        <p className="admin-integrations-field-hint">{t("carriers_oauth_hint")}</p>
                        <SecretField
                            label={t("carriers_client_secret")}
                            hint={t("carriers_client_secret_hint")}
                            secret={account.password}
                            minLength={1}
                            onSave={(plaintext) => patch({ password: plaintext })}
                            onClear={() => patch({ password: null })}
                        />

                    </>
                ) : credentialKind === "raben" ? (
                    <RabenAccountFields account={account} environment={environment} saving={saving} readOnly={readOnly} patch={patch} />
                ) : credentialKind === "jas" ? (
                    <>
                        <p className="admin-integrations-field-hint carrier-card-note">
                            {t(environment === "live" ? "carriers_jas_live_credentials_note" : "carriers_jas_test_credentials_note")}
                        </p>
                        {/* JAS ShipmentGateway: OAuth password grant — client id +
                            client secret (the api_key cipher) and the Extranet 2
                            username + password; the shipper's NIP goes on the
                            loading place's payer block (B2B). */}
                        <div className="carrier-account-times">
                            {([
                                ["clientId", "carriers_client_id", "501986"],
                                ["loginUsername", "carriers_login_username", "name@company.pl"],
                                ["vatNumber", "carriers_vat_number", "1234567890"],
                            ] as const).map(([key, labelKey, placeholder]) => (
                                <div className="admin-integrations-field" key={key}>
                                    <label className="admin-integrations-field-label">{t(labelKey)}</label>
                                    <input
                                        className="admin-integrations-input admin-integrations-input--mono"
                                        defaultValue={account[key]}
                                        placeholder={placeholder}
                                        disabled={saving || readOnly}
                                        onBlur={(e) => {
                                            const v = e.target.value.trim();
                                            if (v !== account[key]) patch({ [key]: v || null }).catch(() => {});
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                        <p className="admin-integrations-field-hint">{t("carriers_login_username_hint_jas")}</p>
                        <SecretField
                            label={t("carriers_jas_client_secret")}
                            hint={t("carriers_client_secret_hint")}
                            secret={account.apiKey}
                            minLength={1}
                            onSave={(plaintext) => patch({ apiKey: plaintext })}
                            onClear={() => patch({ apiKey: null })}
                        />
                        <SecretField
                            label={t("carriers_password")}
                            hint={t("carriers_password_hint")}
                            secret={account.password}
                            minLength={1}
                            onSave={(plaintext) => patch({ password: plaintext })}
                            onClear={() => patch({ password: null })}
                        />
                    </>
                ) : credentialKind === "dpd" || credentialKind === "cargus" ? (
                    <>
                        {credentialKind === "dpd" && (
                            <p className="admin-integrations-field-hint carrier-card-note">
                                {t(environment === "live" ? "carriers_dpd_live_credentials_note" : "carriers_dpd_demo_credentials_note")}
                            </p>
                        )}
                        {/* DPD Poland: login + password + master FID (the
                            x-dpd-fid header) + payer FID. Cargus: username +
                            password + the APIM subscription key + the pickup
                            point every AWB collects from. */}
                        <div className="carrier-account-times">
                            <div className="admin-integrations-field">
                                <label className="admin-integrations-field-label">{t("carriers_login_username")}</label>
                                <input
                                    className="admin-integrations-input admin-integrations-input--mono"
                                    defaultValue={account.loginUsername}
                                    placeholder={credentialKind === "dpd" ? "login" : "user.name"}
                                    disabled={saving || readOnly}
                                    onBlur={(e) => {
                                        const v = e.target.value.trim();
                                        if (v !== account.loginUsername) patch({ loginUsername: v || null }).catch(() => {});
                                    }}
                                />
                            </div>
                            {credentialKind === "dpd" && ([
                                ["masterFid", "carriers_master_fid"],
                                ["payerFid", "carriers_payer_fid"],
                            ] as const).map(([key, labelKey]) => (
                                <div className="admin-integrations-field" key={key}>
                                    <label className="admin-integrations-field-label">{t(labelKey)}</label>
                                    <input
                                        className="admin-integrations-input admin-integrations-input--mono"
                                        type="number"
                                        min={0}
                                        step={1}
                                        defaultValue={account[key] ?? ""}
                                        placeholder="1495"
                                        disabled={saving || readOnly}
                                        onBlur={(e) => {
                                            const raw = e.target.value.trim();
                                            const v = raw === "" ? null : Number(raw);
                                            if (v !== account[key]) patch({ [key]: v }).catch(() => {});
                                        }}
                                    />
                                </div>
                            ))}
                            {credentialKind === "dpd" && (
                                <div className="admin-integrations-field">
                                    <label className="admin-integrations-field-label">{t("carriers_tracking_channel")}</label>
                                    <input
                                        className="admin-integrations-input admin-integrations-input--mono"
                                        defaultValue={account.trackingChannel ?? ""}
                                        disabled={saving || readOnly}
                                        onBlur={(e) => {
                                            const v = e.target.value.trim();
                                            if (v !== (account.trackingChannel ?? "")) patch({ trackingChannel: v || null }).catch(() => {});
                                        }}
                                    />
                                </div>
                            )}
                            {credentialKind === "cargus" && (
                                <div className="admin-integrations-field">
                                    <label className="admin-integrations-field-label">{t("carriers_price_table")}</label>
                                    <input
                                        className="admin-integrations-input admin-integrations-input--mono"
                                        type="number"
                                        min={0}
                                        step={1}
                                        defaultValue={account.priceTableId ?? ""}
                                        placeholder="0"
                                        disabled={saving || readOnly}
                                        onBlur={(e) => {
                                            const raw = e.target.value.trim();
                                            const v = raw === "" ? null : Number(raw);
                                            if (v !== account.priceTableId) patch({ priceTableId: v }).catch(() => {});
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                        <p className="admin-integrations-field-hint">
                            {t(credentialKind === "dpd" ? "carriers_login_username_hint_dpd" : "carriers_login_username_hint_cargus")}
                        </p>
                        {credentialKind === "dpd" && (
                            <p className="admin-integrations-field-hint">{t("carriers_tracking_channel_hint")}</p>
                        )}
                        <SecretField
                            label={t("carriers_password")}
                            hint={t("carriers_password_hint")}
                            secret={account.password}
                            minLength={1}
                            onSave={(plaintext) => patch({ password: plaintext })}
                            onClear={() => patch({ password: null })}
                        />
                        {credentialKind === "cargus" && (
                            <>
                                <SecretField
                                    label={t("carriers_cargus_api_key")}
                                    hint={t("carriers_cargus_api_key_hint")}
                                    secret={account.apiKey}
                                    minLength={1}
                                    onSave={(plaintext) => patch({ apiKey: plaintext })}
                                    onClear={() => patch({ apiKey: null })}
                                />
                                <div className="admin-integrations-field">
                                    <label className="admin-integrations-field-label">{t("carriers_pickup_location")}</label>
                                    <p className="admin-integrations-field-hint">{t("carriers_pickup_location_hint")}</p>
                                    <div className="admin-integrations-test-row">
                                        {pickupLocations === null ? (
                                            <input
                                                className="admin-integrations-input admin-integrations-input--mono"
                                                type="number"
                                                min={0}
                                                step={1}
                                                defaultValue={account.pickupLocationId ?? ""}
                                                placeholder="201266091"
                                                disabled={saving || readOnly}
                                                onBlur={(e) => {
                                                    const raw = e.target.value.trim();
                                                    const v = raw === "" ? null : Number(raw);
                                                    if (v !== account.pickupLocationId) patch({ pickupLocationId: v }).catch(() => {});
                                                }}
                                            />
                                        ) : (
                                            <select
                                                className="admin-integrations-input"
                                                value={account.pickupLocationId ?? ""}
                                                disabled={saving || readOnly}
                                                onChange={(e) => {
                                                    const v = e.target.value === "" ? null : Number(e.target.value);
                                                    patch({ pickupLocationId: v }).catch(() => {});
                                                }}
                                            >
                                                <option value="">{t("carriers_pickup_locations_none")}</option>
                                                {pickupLocations.map((l) => (
                                                    <option key={l.locationId} value={l.locationId}>
                                                        {l.locationId} · {l.name}{l.localityName ? ` (${l.localityName}, ${l.countyName})` : ""}{l.automaticEod ? " · EOD auto" : ""}
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                        <button type="button" className="btn btn-sm" onClick={loadPickupLocations} disabled={loadingLocations || saving || !account.apiKey.set || !account.password.set}>
                                            {loadingLocations && <Loader2 size={14} className="admin-integrations-spinner" />}
                                            {t("carriers_pickup_locations_load")}
                                        </button>
                                    </div>
                                </div>
                                <div className="admin-integrations-test-row">
                                    <button type="button" className="btn btn-sm" onClick={syncNomenclature} disabled={syncingNomenclature || saving || readOnly || !account.apiKey.set || !account.password.set} title={readOnly ? t("read_only_mode") : undefined}>
                                        {syncingNomenclature && <Loader2 size={14} className="admin-integrations-spinner" />}
                                        {t("carriers_nomenclature_sync")}
                                    </button>
                                    {nomenclatureResult && (
                                        <span className="admin-integrations-test-result admin-integrations-test-result--ok">{nomenclatureResult}</span>
                                    )}
                                </div>
                                <p className="admin-integrations-field-hint">{t("carriers_nomenclature_hint")}</p>
                            </>
                        )}
                    </>
                ) : (
                    <>
                        <div className="admin-integrations-field">
                            <label className="admin-integrations-field-label">{t("carriers_login_email")}</label>
                            <p className="admin-integrations-field-hint">{t("carriers_login_email_hint")}</p>
                            <input
                                className="admin-integrations-input"
                                type="email"
                                defaultValue={account.loginEmail}
                                placeholder="name@example.com"
                                disabled={saving || readOnly}
                                onBlur={(e) => {
                                    const v = e.target.value.trim();
                                    if (v !== account.loginEmail) patch({ loginEmail: v || null }).catch(() => {});
                                }}
                            />
                        </div>

                        <SecretField
                            label={t("carriers_password")}
                            hint={t("carriers_password_hint")}
                            secret={account.password}
                            minLength={1}
                            onSave={(plaintext) => patch({ password: plaintext })}
                            onClear={() => patch({ password: null })}
                        />
                    </>
                )}



                <div className="admin-integrations-test-row">
                    <button type="button" className="btn btn-sm" onClick={runTest} disabled={testing || saving || readOnly} title={readOnly ? t("read_only_mode") : undefined}>
                        {testing && <Loader2 size={14} className="admin-integrations-spinner" />}
                        {t("carriers_test_connection")}
                    </button>
                    {testResult && (
                        <span className={`admin-integrations-test-result admin-integrations-test-result--${testResult.ok ? "ok" : "fail"}`}>
                            {testResult.ok
                                ? t("carriers_test_ok", { ms: String(testResult.latencyMs) })
                                : (testResult.error ?? t("carriers_test_failed"))}
                        </span>
                    )}
                    {!testResult && account.status.lastTestAt && (
                        <span className={`admin-integrations-test-result admin-integrations-test-result--${account.status.lastTestStatus === "ok" ? "ok" : "fail"}`}>
                            {account.status.lastTestStatus === "ok"
                                ? t("carriers_last_test_ok")
                                : (account.status.lastTestError ?? t("carriers_test_failed"))}
                        </span>
                    )}
                </div>

                {error && <p className="admin-integrations-field-error">{error}</p>}
                </CarrierSection>

                {connectionOnly && (
                    <p className="admin-integrations-field-hint carrier-card-note">{t(loadingPlace ? (credentialKind === "raben" ? "carriers_raben_sections_note" : "carriers_jas_sections_note") : "carriers_connection_only_note")}</p>
                )}
                {showSender && (
                    <CarrierSection title={t("carriers_section_sender")} summary={senderDigest}>
                        {/* Sender contact + origin address — InPost / DPD /
                            Cargus print these on every label and use the
                            address for undeliverable returns. DPD and Cargus
                            also want the sender COMPANY, and Cargus routes
                            on the sender county. */}
                        {!oauth && (
                            <div className="carrier-field-grid carrier-field-grid--4">
                                {([
                                    ["senderCompany", "carriers_sender_company", "JLI EU Sp. z o.o."],
                                    ["originCounty", "carriers_origin_county", credentialKind === "cargus" ? "Ilfov" : "dolnośląskie"],
                                ] as const).map(([key, labelKey, placeholder]) => (
                                    <div className="admin-integrations-field" key={key}>
                                        <label className="admin-integrations-field-label">{t(labelKey)}</label>
                                        <input
                                            className="admin-integrations-input"
                                            defaultValue={account[key]}
                                            placeholder={placeholder}
                                            disabled={saving || readOnly}
                                            onBlur={(e) => {
                                                const v = e.target.value.trim();
                                                if (v !== account[key]) patch({ [key]: v || null }).catch(() => {});
                                            }}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="carrier-field-grid carrier-field-grid--4">
                            {([
                                ["senderFirstName", "carriers_sender_first_name", "JLI"],
                                ["senderLastName", "carriers_sender_last_name", "Dispatch"],
                                ["senderEmail", "carriers_sender_email", "dispatch@jlitrading.co.uk"],
                                ["senderPhone", "carriers_sender_phone", "+441633000000"],
                            ] as const).map(([key, labelKey, placeholder]) => (
                                <div className="admin-integrations-field" key={key}>
                                    <label className="admin-integrations-field-label">{t(labelKey)}</label>
                                    <input
                                        className="admin-integrations-input"
                                        defaultValue={account[key]}
                                        placeholder={placeholder}
                                        disabled={saving || readOnly}
                                        onBlur={(e) => {
                                            const v = e.target.value.trim();
                                            if (v !== account[key]) patch({ [key]: v || null }).catch(() => {});
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                        <div className="carrier-field-grid carrier-field-grid--4">
                            {([
                                ["originHouseNumber", "carriers_origin_house_number", "Unit 9"],
                                ["originStreet", "carriers_origin_street", "St Modwen Park"],
                                ["originCity", "carriers_origin_city", "Newport"],
                                ["originPostcode", "carriers_origin_postcode", "NP19 4RJ"],
                            ] as const).map(([key, labelKey, placeholder]) => (
                                <div className="admin-integrations-field" key={key}>
                                    <label className="admin-integrations-field-label">{t(labelKey)}</label>
                                    <input
                                        className="admin-integrations-input"
                                        defaultValue={account[key]}
                                        placeholder={placeholder}
                                        disabled={saving || readOnly}
                                        onBlur={(e) => {
                                            const v = e.target.value.trim();
                                            if (v !== account[key]) patch({ [key]: v || null }).catch(() => {});
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                        <p className="admin-integrations-field-hint">{t(oauth ? "carriers_sender_origin_hint" : "carriers_sender_origin_hint_pl")}</p>
                    </CarrierSection>
                )}
                {!connectionOnly && (
                <>
                {showCollection && (
                    <CarrierSection title={t("carriers_section_collection")} summary={collectionDigest}>
                <div className="carrier-account-times">
                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">{t("carriers_ready_at")}</label>
                        <input
                            className="admin-integrations-input admin-integrations-input--time"
                            type="time"
                            defaultValue={account.readyAt ?? ""}
                            disabled={saving || readOnly}
                            onBlur={(e) => {
                                const v = e.target.value || null;
                                if (v !== account.readyAt) patch({ readyAt: v }).catch(() => {});
                            }}
                        />
                    </div>
                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">{t("carriers_closed_at")}</label>
                        <input
                            className="admin-integrations-input admin-integrations-input--time"
                            type="time"
                            defaultValue={account.closedAt ?? ""}
                            disabled={saving || readOnly}
                            onBlur={(e) => {
                                const v = e.target.value || null;
                                if (v !== account.closedAt) patch({ closedAt: v }).catch(() => {});
                            }}
                        />
                    </div>
                </div>
                <p className="admin-integrations-field-hint">{t(credentialKind === "email" ? "carriers_times_hint" : "carriers_times_hint_day_close")}</p>
                    </CarrierSection>
                )}

                <CarrierSection title={t("carriers_section_customs")} summary={customsDigest}>
                <div className="carrier-account-times">
                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">{t("carriers_eori")}</label>
                        <input
                            className="admin-integrations-input admin-integrations-input--mono"
                            defaultValue={account.eoriNumber}
                            placeholder="GB132656618000"
                            disabled={saving || readOnly}
                            onBlur={(e) => {
                                const v = e.target.value.trim().toUpperCase();
                                if (v !== account.eoriNumber) patch({ eoriNumber: v || null }).catch(() => {});
                            }}
                        />
                    </div>
                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">{t("carriers_ioss")}</label>
                        <div className="admin-integrations-form-row--toggle">
                            <label className="admin-settings-toggle">
                                <input
                                    type="checkbox"
                                    checked={account.ioss}
                                    disabled={saving || readOnly}
                                    onChange={(e) => { patch({ ioss: e.target.checked }).catch(() => {}); }}
                                />
                                <span className="admin-settings-slider" />
                            </label>
                        </div>
                    </div>
                </div>
                <p className="admin-integrations-field-hint">{t(oauth ? "carriers_eori_hint_inpost" : "carriers_eori_hint")}</p>

                <div className="carrier-account-times">
                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">{t("carriers_duty_description")}</label>
                        <input
                            className="admin-integrations-input"
                            defaultValue={account.dutyDescription}
                            placeholder="home"
                            disabled={saving || readOnly}
                            onBlur={(e) => {
                                const v = e.target.value.trim();
                                if (v !== account.dutyDescription) patch({ dutyDescription: v || null }).catch(() => {});
                            }}
                        />
                    </div>
                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">{t("carriers_duty_hts")}</label>
                        <input
                            className="admin-integrations-input admin-integrations-input--mono"
                            defaultValue={account.dutyHtsCode}
                            placeholder="9505900000"
                            disabled={saving || readOnly}
                            onBlur={(e) => {
                                const v = e.target.value.trim();
                                if (v !== account.dutyHtsCode) patch({ dutyHtsCode: v || null }).catch(() => {});
                            }}
                        />
                    </div>
                    <div className="admin-integrations-field">
                        <label className="admin-integrations-field-label">{t("carriers_duty_origin")}</label>
                        <input
                            className="admin-integrations-input"
                            defaultValue={account.dutyOrigin}
                            placeholder="United Kingdom"
                            disabled={saving || readOnly}
                            onBlur={(e) => {
                                const v = e.target.value.trim();
                                if (v !== account.dutyOrigin) patch({ dutyOrigin: v || null }).catch(() => {});
                            }}
                        />
                    </div>
                </div>
                <p className="admin-integrations-field-hint">{t(oauth ? "carriers_duty_hint_inpost" : "carriers_duty_hint")}</p>
                </CarrierSection>

                {error && <p className="admin-integrations-field-error">{error}</p>}

                {(credentialKind === "dpd" || credentialKind === "cargus") && (
                    <CarrierSection
                        title={t("carriers_section_test_booking")}
                        summary={testBookingResult ? (testBookingResult.ok ? t("carriers_test_booking_ok") : t("carriers_test_booking_failed")) : null}
                        badge={testBookingResult ? { text: testBookingResult.ok ? t("carriers_badge_on") : t("carriers_badge_off"), tone: testBookingResult.ok ? "ok" : "warn" } : null}
                    >
                        <p className="admin-integrations-field-hint">{t("carriers_test_booking_hint")}</p>
                        <p className="admin-integrations-field-hint">
                            {t(credentialKind === "dpd" ? "carriers_test_booking_hint_dpd" : "carriers_test_booking_hint_cargus")}
                        </p>
                        <label className="admin-integrations-field-label">{t("carriers_test_booking_recipient")}</label>
                        <div className="carrier-field-grid carrier-field-grid--4">
                            {([
                                ["name", "carriers_test_booking_name"],
                                ["line1", "carriers_test_booking_line1"],
                                ["line2", "carriers_test_booking_line2"],
                                ["city", "carriers_test_booking_city"],
                                ...(credentialKind === "cargus" ? [["county", "carriers_test_booking_county"] as const] : []),
                                ["postcode", "carriers_test_booking_postcode"],
                                ...(credentialKind === "dpd" ? [["country", "carriers_test_booking_country"] as const] : []),
                                ["phone", "carriers_test_booking_phone"],
                                ["email", "carriers_test_booking_email"],
                            ] as const).map(([key, labelKey]) => (
                                <div className="admin-integrations-field" key={key}>
                                    <label className="admin-integrations-field-label">{t(labelKey)}</label>
                                    <input
                                        className="admin-integrations-input"
                                        value={testRecipient[key]}
                                        disabled={testBooking || readOnly}
                                        onChange={(e) => setTestRecipient((r) => ({ ...r, [key]: e.target.value }))}
                                    />
                                </div>
                            ))}
                        </div>
                        <label className="admin-integrations-field-label">{t("carriers_test_booking_box")}</label>
                        <div className="carrier-field-grid carrier-field-grid--4">
                            {([
                                ["weight_grams", "carriers_test_booking_weight"],
                                ["length_mm", "carriers_test_booking_length"],
                                ["width_mm", "carriers_test_booking_width"],
                                ["height_mm", "carriers_test_booking_height"],
                            ] as const).map(([key, labelKey]) => (
                                <div className="admin-integrations-field" key={key}>
                                    <label className="admin-integrations-field-label">{t(labelKey)}</label>
                                    <input
                                        className="admin-integrations-input admin-integrations-input--mono"
                                        type="number"
                                        min={1}
                                        step={1}
                                        value={testBox[key]}
                                        disabled={testBooking || readOnly}
                                        onChange={(e) => setTestBox((b) => ({ ...b, [key]: Number(e.target.value) || 0 }))}
                                    />
                                </div>
                            ))}
                        </div>
                        <div className="admin-integrations-test-row">
                            <select
                                className="admin-integrations-input"
                                value={testServiceId}
                                disabled={testBooking || readOnly}
                                onChange={(e) => setTestServiceId(e.target.value)}
                                aria-label={t("carriers_test_booking_service")}
                            >
                                <option value="">{t("carriers_test_booking_service_default")}</option>
                                {account.services.filter((s) => s.enabled).map((s) => (
                                    <option key={s.id} value={String(s.id)}>{s.serviceCode} — {s.displayName}</option>
                                ))}
                            </select>
                            <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={runTestBooking}
                                disabled={testBooking || saving || readOnly || !account.password.set}
                                title={readOnly ? t("read_only_mode") : undefined}
                            >
                                {testBooking && <Loader2 size={14} className="admin-integrations-spinner" />}
                                {testBooking ? t("carriers_test_booking_running") : t("carriers_test_booking_run")}
                            </button>
                        </div>
                        {testBookingResult && (
                            <div className="carrier-test-booking-result">
                                <span className={`admin-integrations-test-result admin-integrations-test-result--${testBookingResult.ok ? "ok" : "fail"}`}>
                                    {testBookingResult.ok ? t("carriers_test_booking_ok") : t("carriers_test_booking_failed")} · {testBookingResult.carrier} · {testBookingResult.environment}
                                </span>
                                {testBookingResult.warnings.map((w) => (
                                    <p className="admin-integrations-field-error" key={w}>{w}</p>
                                ))}
                                {testBookingResult.service && (
                                    <p className={testBookingResult.service.source === "carrier_default" ? "admin-integrations-field-error" : "admin-integrations-field-hint"}>
                                        {t("carriers_test_booking_service_used")}: <span className="admin-integrations-input--mono">{testBookingResult.service.code}</span>
                                        {testBookingResult.service.name ? ` — ${testBookingResult.service.name}` : ""}
                                        {testBookingResult.service.source !== "picked" && (
                                            <> · {t(`carriers_test_booking_service_source_${testBookingResult.service.source}`, { rule: testBookingResult.service.ruleName ?? "" })}</>
                                        )}
                                    </p>
                                )}
                                <label className="admin-integrations-field-label">{t("carriers_test_booking_steps")}</label>
                                <ul className="carrier-test-booking-steps">
                                    {testBookingResult.steps.map((s) => (
                                        <li key={s.name} className={s.ok ? "carrier-test-booking-step--ok" : "carrier-test-booking-step--fail"}>
                                            <span className="admin-integrations-input--mono">{s.name}</span>
                                            {" — "}{s.ok ? "OK" : "FAIL"}{s.status != null ? ` (HTTP ${s.status})` : ""} · {s.ms} ms
                                            {s.error && <>: {s.error}</>}
                                        </li>
                                    ))}
                                </ul>
                                {testBookingResult.trackingNumbers.length > 0 && (
                                    <p className="admin-integrations-field-hint">
                                        {t("carriers_test_booking_tracking")}: <span className="admin-integrations-input--mono">{testBookingResult.trackingNumbers.join(", ")}</span>
                                    </p>
                                )}
                                {testBookingResult.cleanup && (
                                    <p className={testBookingResult.cleanup.deleted === false ? "admin-integrations-field-error" : "admin-integrations-field-hint"}>
                                        {t("carriers_test_booking_cleanup")}: {testBookingResult.cleanup.note}
                                    </p>
                                )}
                                {testBookingResult.label && (
                                    <button type="button" className="btn btn-sm" onClick={() => downloadTestLabel(testBookingResult.label!)}>
                                        {t("carriers_test_booking_download", { format: testBookingResult.label.format })}
                                    </button>
                                )}
                            </div>
                        )}
                    </CarrierSection>
                )}

                {/* Services */}
                <CarrierSection
                    title={t("carriers_services_heading")}
                    summary={servicesDigest}
                    badge={account.services.length === 0 ? { text: t("carriers_badge_none"), tone: "warn" } : null}
                    defaultOpen={account.services.length === 0}
                >
                <div className="admin-integrations-field">
                    <p className="admin-integrations-field-hint">{t("carriers_services_hint")}</p>
                    {account.services.length === 0 && (
                        <p className="admin-integrations-vendors-empty">{t("carriers_services_empty")}</p>
                    )}
                    {account.services.length > 0 && (
                        <div className="carrier-service-table">
                            <div className="carrier-service-grid carrier-service-grid--header">
                                <span title={t("carriers_service_enabled")}>{t("carriers_service_col_on")}</span>
                                <span>{t("carriers_service_code")}</span>
                                <span>{t("carriers_service_name")}</span>
                                <span>{t("carriers_service_col_type")}</span>
                                <span>{t("carriers_service_max_pieces")}</span>
                                <span>{t("carriers_service_max_kg")}</span>
                                <span>{t("carriers_service_max_l")}</span>
                                <span>{t("carriers_service_max_w")}</span>
                                <span>{t("carriers_service_max_h")}</span>
                                {bandPricing ? (
                                    <>
                                        <span>{t("carriers_service_priority")}</span>
                                        <span>{t("carriers_service_price_s")}</span>
                                        <span>{t("carriers_service_price_m")}</span>
                                        <span>{t("carriers_service_price_l")}</span>
                                    </>
                                ) : (
                                    <>
                                        <span>{t("carriers_service_base_cost")}</span>
                                        <span>{t("carriers_service_add_parcel_cost")}</span>
                                        <span>{t("carriers_service_included_kg")}</span>
                                        <span>{t("carriers_service_per_kg")}</span>
                                    </>
                                )}
                                <span />
                            </div>
                            {account.services.map((s) => (
                                <ServiceRow key={s.id} service={s} carrierCode={carrierCode} onUpdated={onUpdated} />
                            ))}
                        </div>
                    )}
                    {!addingService ? (
                        <button type="button" className="btn btn-sm" onClick={() => setAddingService(true)} disabled={saving || readOnly}>
                            <Plus size={14} /> {t("carriers_service_add")}
                        </button>
                    ) : (
                        <div className="carrier-service-add">
                            <div className="carrier-service-add-fields">
                                <div className="admin-integrations-field">
                                    <label className="admin-integrations-field-label">{t("carriers_service_pick")}</label>
                                    <select
                                        className="admin-integrations-input"
                                        value={newCatalogueCode}
                                        onChange={(e) => pickCatalogueService(e.target.value)}
                                        disabled={saving || readOnly}
                                    >
                                        <option value="">{t("carriers_service_pick_custom")}</option>
                                        {catalogue.map((s) => (
                                            <option key={s.code} value={s.code}>
                                                {s.code} — {s.name}
                                                {s.baseCost != null ? ` (£${s.baseCost.toFixed(2)}${s.surchargeNote ? ` ${s.surchargeNote}` : ""})` : ""}
                                            </option>
                                        ))}
                                    </select>
                                    <p className="admin-integrations-field-hint">{t("carriers_service_pick_hint")}</p>
                                </div>
                                <div className="admin-integrations-field">
                                    <label className="admin-integrations-field-label">{t("carriers_service_code")}</label>
                                    <input
                                        className="admin-integrations-input admin-integrations-input--mono"
                                        value={newCode}
                                        placeholder="NPND16"
                                        onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                                        disabled={saving || readOnly}
                                    />
                                </div>
                                <div className="admin-integrations-field">
                                    <label className="admin-integrations-field-label">{t("carriers_service_name")}</label>
                                    <input
                                        className="admin-integrations-input"
                                        value={newName}
                                        onChange={(e) => setNewName(e.target.value)}
                                        disabled={saving || readOnly}
                                    />
                                </div>
                                <div className="admin-integrations-field">
                                    <label className="admin-integrations-field-label">{t("carriers_service_base_cost")}</label>
                                    <input
                                        type="number" min={0} step={0.01}
                                        className="admin-integrations-input"
                                        value={newBaseCost}
                                        placeholder="4.44"
                                        onChange={(e) => setNewBaseCost(e.target.value)}
                                        disabled={saving || readOnly}
                                    />
                                </div>
                                <div className="admin-integrations-field">
                                    <label className="admin-integrations-field-label">{t("carriers_service_add_parcel_cost")}</label>
                                    <input
                                        type="number" min={0} step={0.01}
                                        className="admin-integrations-input"
                                        value={newAddCost}
                                        placeholder="0.25"
                                        onChange={(e) => setNewAddCost(e.target.value)}
                                        disabled={saving || readOnly}
                                    />
                                </div>
                            </div>
                            <div className="carrier-service-add-actions">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-primary"
                                    onClick={addService}
                                    disabled={saving || readOnly || !newCode.trim() || !newName.trim()}
                                    title={readOnly ? t("read_only_mode") : undefined}
                                >
                                    <Plus size={14} /> {t("carriers_service_add")}
                                </button>
                                <button type="button" className="btn btn-sm" onClick={() => setAddingService(false)} disabled={saving || readOnly}>
                                    {t("cancel")}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
                </CarrierSection>
                </>
                )}
            </div>
        </details>
    );
};

export default CarrierAccountPanel;
